import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Linkedin, Twitter, Facebook, Instagram, Mail, Phone, MapPin } from 'lucide-react';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { useTranslation } from '@/features/i18n';

export function Footer() {
  const { data: settings } = useSiteSettings();
  const { t } = useTranslation();

  const footerSections = [
    {
      title: t.footer.services,
      links: [
        { label: t.nav.services.crm, href: '/services/crm' },
        { label: t.nav.services.app, href: '/services/app' },
        { label: t.nav.services.web, href: '/services/web' },
        { label: t.nav.services.sms, href: '/services/sms-marketing' },
        { label: t.nav.services.voice, href: '/services/voice-agent' },
      ],
    },
    {
      title: t.footer.company,
      links: [
        { label: t.nav.about, href: '/about' },
        { label: t.nav.caseStudies, href: '/case-studies' },
        { label: t.nav.blog, href: '/blog' },
        { label: t.nav.contact, href: '/contact' },
      ],
    },
    {
      title: t.footer.legal,
      links: [
        { label: t.footer.privacy, href: '/privacy-policy' },
        { label: t.footer.terms, href: '/terms' },
      ],
    },
  ];

  const socialLinks = [
    { icon: Linkedin, href: settings?.social_linkedin, label: 'LinkedIn' },
    { icon: Twitter, href: settings?.social_twitter, label: 'Twitter' },
    { icon: Facebook, href: settings?.social_facebook, label: 'Facebook' },
    { icon: Instagram, href: settings?.social_instagram, label: 'Instagram' },
  ].filter((link) => link.href);

  return (
    <footer className="bg-[#0a0a0a]">
      {/* Pre-footer CTA Section - ninito style */}
      <div className="border-b border-white/10">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-20 py-20 lg:py-28">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10">
            <div className="max-w-[600px]">
              <h2 className="text-[32px] md:text-[40px] lg:text-[48px] font-semibold text-white tracking-[-0.02em] leading-tight">
                {t.home.cta.title}
              </h2>
              <p className="mt-4 text-lg text-white/50 leading-relaxed">
                {t.home.cta.description}
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 px-8 py-4 bg-white text-black font-medium rounded-full hover:bg-white/90 transition-all group"
            >
              {t.home.cta.button}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>

      {/* Main footer content */}
      <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-20 py-16 lg:py-20">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-10 lg:gap-16">
          {/* Brand column */}
          <div className="col-span-2 md:col-span-4 lg:col-span-2">
            <Link to="/" className="inline-block">
              <img
                src="/logo.png"
                alt={settings?.site_name || 'Din AI Bedrift'}
                className="h-18 w-auto brightness-0 invert"
              />
            </Link>
            <p className="mt-5 text-white/40 leading-relaxed max-w-sm text-[15px]">
              {settings?.site_description || t.footer.description}
            </p>

            {/* Contact info - compact style */}
            <div className="mt-8 space-y-3">
              {settings?.contact_email && (
                <a
                  href={`mailto:${settings.contact_email}`}
                  className="flex items-center text-white/40 hover:text-white transition-colors text-sm group"
                >
                  <Mail className="w-4 h-4 mr-3 text-white/30 group-hover:text-[#0078ff]" />
                  {settings.contact_email}
                </a>
              )}
              {settings?.contact_phone && (
                <a
                  href={`tel:${settings.contact_phone.replace(/\s/g, '')}`}
                  className="flex items-center text-white/40 hover:text-white transition-colors text-sm group"
                >
                  <Phone className="w-4 h-4 mr-3 text-white/30 group-hover:text-[#0078ff]" />
                  {settings.contact_phone}
                </a>
              )}
              {settings?.contact_address && (
                <div className="flex items-center text-white/40 text-sm">
                  <MapPin className="w-4 h-4 mr-3 text-white/30" />
                  {settings.contact_address}
                </div>
              )}
            </div>

            {/* Social links */}
            {socialLinks.length > 0 && (
              <div className="flex items-center gap-2 mt-8">
                {socialLinks.map(({ icon: Icon, href, label }) => (
                  <a
                    key={label}
                    href={href!}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-white/5 text-white/40 hover:text-white hover:bg-white/10 transition-all duration-200 flex items-center justify-center"
                    aria-label={label}
                  >
                    <Icon className="w-[18px] h-[18px]" />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Links columns */}
          {footerSections.map((section) => (
            <div key={section.title}>
              <h3 className="text-sm font-semibold text-white/70 uppercase tracking-wider mb-5">
                {section.title}
              </h3>
              <ul className="space-y-3">
                {section.links.map((link) => (
                  <li key={link.href}>
                    <Link
                      to={link.href}
                      className="text-white/40 hover:text-white transition-colors text-[15px] flex items-center group"
                    >
                      {link.label}
                      <ArrowUpRight className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div className="pt-10 mt-16 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/30 text-sm">
            {settings?.footer_text || t.footer.copyright}
            {' '}
            <span className="text-white/20">|</span>
            {' '}
            Developed by{' '}
            <a
              href="https://tausifmasud.me/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/50 hover:text-white transition-colors"
            >
              Tausif Masud
            </a>
          </p>
          <div className="flex items-center gap-6 text-sm text-white/30">
            <Link to="/privacy-policy" className="hover:text-white transition-colors">
              {t.footer.privacy}
            </Link>
            <Link to="/terms" className="hover:text-white transition-colors">
              {t.footer.terms}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
